// 2026-10-13: popup koszyka jako modal <dialog> (showModal -> top layer) z kartami "Kupowane razem".
// Karty renderuje Liquid (snippets/cart-xsell-popup.liquid w sekcji cart-notification-product);
// ten plik tylko kopiuje gotowy HTML po kluczu linii i obsluguje stepper + "Dodaj do koszyka".
class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.xsellHost = document.getElementById('cart-notification-xsell');
    this.header = document.querySelector('sticky-header');

    this.querySelectorAll('[data-cxp-close]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );

    // Klik w ::backdrop: target to sam <dialog>, a punkt lezy poza jego prostokatem
    // (klik w padding dialogu tez ma target === dialog, wiec sprawdzamy wspolrzedne).
    this.notification.addEventListener('click', (evt) => {
      if (evt.target !== this.notification) return;
      const rect = this.notification.getBoundingClientRect();
      const inside =
        evt.clientX >= rect.left && evt.clientX <= rect.right && evt.clientY >= rect.top && evt.clientY <= rect.bottom;
      if (!inside) this.close();
    });

    // Escape: natywny 'cancel' zamyka dialog; sprzatanie w 'close' (wspolne dla kazdej sciezki zamkniecia).
    this.notification.addEventListener('close', () => this.onDialogClosed());

    if (this.xsellHost) {
      this.xsellHost.addEventListener('click', this.onXsellClick.bind(this));
      this.xsellHost.addEventListener('change', this.onXsellChange.bind(this));
    }
  }

  open() {
    if (!this.notification.open) {
      this.notification.showModal();
      this.dispatchCartViewEvent();
    }
    document.documentElement.classList.add('cxp-open');
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    if (this.notification.open) this.notification.close();
    else this.onDialogClosed();
  }

  onDialogClosed() {
    document.documentElement.classList.remove('cxp-open');
    const active = this.activeElement;
    this.activeElement = null;
    if (active && document.contains(active) && (!document.activeElement || document.activeElement === document.body)) {
      active.focus({ preventScroll: true });
    }
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const sections = parsedState.sections || {};

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const html = sections[section.id];
      if (!target || !html) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner !== null) target.innerHTML = inner;
    });

    // Karty "Kupowane razem" dla dodanej pozycji: ta sama sekcja co produkt
    // (cart-notification-product), blok po kluczu linii. Celowo poza getSectionsToRender(),
    // bo product-form.js wysyla id z tej listy jako nazwy sekcji do /cart/add.js.
    if (this.xsellHost) {
      const html = sections['cart-notification-product'];
      const inner = html ? this.getSectionInnerHTML(html, `[id="cart-notification-xsell-${this.cartItemKey}"]`) : null;
      this.xsellHost.innerHTML = inner ? inner.trim() : '';
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  onXsellChange(evt) {
    const input = evt.target.closest('[data-cxp-qty]');
    if (input) input.value = this.clampQty(input.value);
  }

  onXsellClick(evt) {
    const step = evt.target.closest('[data-cxp-step]');
    if (step) {
      evt.preventDefault();
      const card = step.closest('[data-xsell-card]');
      const input = card && card.querySelector('[data-cxp-qty]');
      if (input) input.value = this.clampQty((parseInt(input.value, 10) || 1) + parseInt(step.dataset.cxpStep, 10));
      return;
    }

    const add = evt.target.closest('[data-xsell-add]');
    if (add && add.tagName === 'BUTTON') {
      evt.preventDefault();
      this.addFromCard(add);
    }
  }

  clampQty(value) {
    const n = parseInt(value, 10);
    if (isNaN(n) || n < 1) return 1;
    return Math.min(10, n);
  }

  async addFromCard(button) {
    if (button.disabled) return;
    const card = button.closest('[data-xsell-card]');
    const input = card && card.querySelector('[data-cxp-qty]');
    const errorEl = card && card.querySelector('[data-cxp-error]');
    const variantId = button.dataset.variantId;
    const quantity = this.clampQty(input ? input.value : 1);

    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', quantity);
    body.append(
      'sections',
      this.getSectionsToRender()
        .map((section) => section.id)
        .join(',')
    );
    body.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const json = await response.json();

      if (json.status) {
        this.showCardError(errorEl, json.description || json.message);
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-notification-xsell',
          productVariantId: variantId,
          cartData: json,
        });
      }

      this.renderContents(json);
      this.notification.scrollTop = 0;
      const title = document.getElementById('cxp-title');
      if (title) title.focus({ preventScroll: true });
    } catch (e) {
      console.error(e);
      this.showCardError(errorEl, window.cartStrings?.error);
    } finally {
      if (document.contains(button)) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  showCardError(errorEl, message) {
    if (!errorEl || !message) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
